import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { MatSnackBar } from '@angular/material/snack-bar'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject } from 'rxjs'
import { takeUntil, tap } from 'rxjs/operators'

import { MessengerApiService } from '@app/modules/client/messenger/services/messenger-api.service'
import { MessengerService } from '@app/modules/client/messenger/services/messenger.service'
import { DestroyService } from '@core/services/destroy.service'
import { User } from '@shared/models/user.model'
import { ChatMessageFormData, ChatMessageModel, ChatUserModel } from '@shared/models/messenger/messenger.model'
import { MessengerDraftService } from '../../services/messanger-draft.service'

@Component({
  selector: 'lin-chat-form',
  templateUrl: './chat-form.component.html',
  styleUrls: ['./chat-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatFormComponent implements OnInit {
  @Input() chatId: number
  @Input() currentUser: User
  @Input() typing: ChatUserModel | null
  @Output() handleTyping = new EventEmitter<boolean>()
  @Output() isAttachment = new EventEmitter<boolean>()

  picture: string | ArrayBuffer | SafeUrl
  attachmentName: string
  form: FormGroup
  loading$ = new BehaviorSubject<boolean>(false)
  editedMessage: Pick<ChatMessageModel, 'id' | 'message'>

  get id() {
    return this.route.snapshot
  }

  get isShowAttachment(): boolean {
    return !!(this.picture || this.editedMessage?.id || this.attachmentName)
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly messengerApiService: MessengerApiService,
    private readonly messengerService: MessengerService,
    private readonly destroyService: DestroyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly messengerDraftService: MessengerDraftService,
    private readonly route: ActivatedRoute,
    private readonly sanitize: DomSanitizer,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initForm()
    this.subscribeInputValueChange()
    this.subscribeMessageEdit()

    this.route.params.pipe(takeUntil(this.destroyService)).subscribe(() => {
      this.initFormData()
    })
  }

  initFormData(): void {
    const draftState = this.messengerDraftService.getState()[this.chatId]
    this.loading$.next(false)
    this.picture = draftState?.picture || ''
    this.form.setValue({
      message: draftState?.message || '',
      attachment: draftState?.attachment || '',
    })
  }

  handleSendMessage(): void {
    this.loading$.next(true)
    const { message, attachment } = <ChatMessageFormData>this.form.value

    if (!message && !attachment) return

    const postMessageSubscribe = (): void => {
      this.form.setValue({
        message: '',
        attachment: '',
      })
      this.removeAttachment()
      this.form.setErrors({ server: false })
      this.messengerService.setChatMessage(null)
      this.loading$.next(false)
      this.messengerService.triggerScrollToBottom()
    }

    const errorMessageSubscribe = (): void => {
      const errorMessage = <string>this.translateService.instant('MAIN.ERROR_PAGE_TITLE')

      this.form.setErrors({ server: true })
      this.loading$.next(false)
      this.snackBar.open(errorMessage, '', { duration: 3000, panelClass: 'lin-form-error' })
    }

    const dataToSave = {
      ...(message && { message }),
      ...(attachment && { attachment }),
    }

    const messageAction = this.editedMessage
      ? this.messengerApiService.editChatMessage(this.chatId, this.editedMessage.id, dataToSave)
      : this.messengerApiService.postMessage(this.chatId, dataToSave)

    messageAction.pipe(takeUntil(this.destroyService)).subscribe({ next: postMessageSubscribe, error: errorMessageSubscribe })
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      message: '',
      attachment: '',
    })
  }

  private subscribeInputValueChange(): void {
    let timer: ReturnType<typeof setTimeout>

    const typingToggleTimeoutHandler = (toggle: boolean): VoidFunction => {
      return () => this.handleTyping.emit(toggle)
    }

    const messageChange = ({ attachment, message }: ChatMessageFormData) => {
      clearTimeout(timer)
      timer = setTimeout(typingToggleTimeoutHandler(false), 1000)
      this.messengerDraftService.setState({ [this.chatId]: { message, attachment, picture: this.picture } })
    }

    const emitTyping = ({ attachment, message }: ChatMessageFormData): void => this.handleTyping.emit(!!attachment || !!message)

    this.form.valueChanges
      .pipe(
        tap(() => clearTimeout(timer)),
        tap(emitTyping),
        takeUntil(this.destroyService),
      )
      .subscribe(messageChange)
  }

  handleCancelMessageEdit(): void {
    this.messengerService.setChatMessage(null)
    this.cdr.markForCheck()
  }

  private subscribeMessageEdit(): void {
    this.messengerService.messageUpdate$.pipe(takeUntil(this.destroyService)).subscribe((message) => {
      message?.id && this.removeAttachment()
      this.editedMessage = message
      this.form.patchValue({ message: message?.message || '' })
      this.isAttachment.emit(true)
    })
  }

  uploadFile(files: FileList): void {
    if (files && files.length) {
      const reader = new FileReader()

      reader.readAsDataURL(files[0])
      reader.onload = (event) => {
        if (files[0].type.includes('image')) {
          this.picture = this.sanitize.bypassSecurityTrustUrl(<string>event.target.result)
          this.attachmentName = null
        } else {
          this.attachmentName = files[0].name
          this.picture = null
        }
        this.form.patchValue({ attachment: files[0] })
        this.isAttachment.emit(true)
        this.cdr.markForCheck()
      }
    }
  }

  removeAttachment(): void {
    this.picture = null
    this.attachmentName = null
    this.form.patchValue({ attachment: null })
    this.isAttachment.emit(false)
    this.cdr.markForCheck()
  }
}
